// FR-W8: category confirmation. Guesses render as toggle pills (top guess
// arrives pre-selected from the store); "change" expands the full
// subcategory picker fed from the stash-scoped category tree.
import { useState } from 'react';
import type { CategoryGuess, StashCategory } from '@stash/shared-types';

interface CategoryPillsProps {
  guesses: CategoryGuess[];
  selected: StashCategory[];
  allCategories: StashCategory[];
  onToggle: (category: StashCategory) => void;
}

export function CategoryPills({ guesses, selected, allCategories, onToggle }: CategoryPillsProps): React.JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const isSelected = (category: StashCategory): boolean =>
    selected.some((entry) => entry.recordID === category.recordID);

  const guessIds = new Set(guesses.map((guess) => guess.category.recordID));
  const subcategories = allCategories.filter(
    (category) => category.parentCategoryID && !guessIds.has(category.recordID));

  const pill = (category: StashCategory): React.JSX.Element => (
    <button
      key={category.recordID}
      type="button"
      aria-pressed={isSelected(category)}
      onClick={() => onToggle(category)}
      className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
        isSelected(category)
          ? 'bg-primary text-primary-foreground'
          : 'bg-secondary text-secondary-foreground hover:bg-[var(--stash-blue-light)]'
      }`}
    >
      {category.name}
    </button>
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">Category (pick at least one)</p>
      <div className="flex flex-wrap items-center gap-2">
        {guesses.map((guess) => pill(guess.category))}
        {selected.filter((entry) => !guessIds.has(entry.recordID)).map(pill)}
        <button
          type="button"
          onClick={() => setPickerOpen((open) => !open)}
          className="rounded-full border border-dashed border-border px-3 py-1 text-sm text-muted-foreground"
        >
          change
        </button>
      </div>
      {pickerOpen && (
        <div className="flex flex-wrap gap-2 rounded-2xl border border-border bg-[var(--stash-surface-sunken)] p-3">
          {subcategories.map(pill)}
        </div>
      )}
    </div>
  );
}
